import Rail from 'app/ui/components/molecules/rail';
import ClassCard, { ClassCardContainer } from 'app/ui/components/atoms/class-card';
import SimplifiedClassCard from 'app/ui/components/atoms/class-card/simplified';
import RestDayCard, { isRestDay } from 'app/ui/components/molecules/rest-day-card';
import type { Workout } from 'app/ui/components/molecules/workout-list';
import { routes } from 'app/routes/constants';
import { useMutation } from '@apollo/client';
import { useAppState } from 'state';
import type {
  SaveLessonByIdMutation,
  SaveLessonByIdMutationVariables,
  UnsaveLessonByIdMutation,
  UnsaveLessonByIdMutationVariables,
} from 'app/types/graphql';
import {
  SaveLessonByIdClassCard as SAVE_LESSON,
  UnsaveLessonByIdClassCard as UNSAVE_LESSON,
} from 'app/ui/components/atoms/class-card/mutations.gql';

type WorkoutRailProps = {
  title: React.ReactNode;
  description?: string | null;
  workouts: Workout[];
  onCardClick?: (workout: Workout) => void;
  testId?: string;
  cardTestIdPrefix?: string;
  showProgress?: boolean;
  cardVariant?: 'full' | 'simplified';
};

const WorkoutRail: React.FC<WorkoutRailProps> = ({
  title,
  description,
  workouts,
  onCardClick,
  testId,
  cardTestIdPrefix,
  showProgress = true,
  cardVariant = 'full',
}) => {
  const userId = useAppState(({ auth }) => auth.userId);

  const [saveLesson] = useMutation<SaveLessonByIdMutation, SaveLessonByIdMutationVariables>(SAVE_LESSON);
  const [unsaveLesson] = useMutation<UnsaveLessonByIdMutation, UnsaveLessonByIdMutationVariables>(UNSAVE_LESSON);

  const handleSaveLesson = async (lessonId: string): Promise<{ isSaved: boolean }> => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    const result = await saveLesson({
      variables: { id: lessonId, input: { user: { id: userId } } },
    });
    return { isSaved: result.data?.saveLessonById.isSaved ?? false };
  };

  const handleUnsaveLesson = async (lessonId: string): Promise<{ isSaved: boolean }> => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    const result = await unsaveLesson({
      variables: { id: lessonId, input: { user: { id: userId } } },
    });
    return { isSaved: result.data?.unsaveLessonById.isSaved ?? false };
  };

  return (
    <Rail title={title} description={description} testId={testId} showProgress={showProgress}>
      {workouts.map((workout, idx) => {
        if (isRestDay(workout.id)) {
          return (
            <ClassCardContainer key={`${workout.id}-${idx}`}>
              <RestDayCard
                label={workout.label}
                testId={
                  cardTestIdPrefix
                    ? `${cardTestIdPrefix}-${idx}`
                    : testId
                      ? `${testId}-card-${idx}`
                      : `workout-rail-card-${idx}`
                }
              />
            </ClassCardContainer>
          );
        }

        return (
          <ClassCardContainer key={`${workout.id}-${idx}`}>
            {cardVariant === 'full' ? (
              <ClassCard
                lessonId={workout.id}
                srcSet={workout.srcSet}
                title={workout.title}
                to={routes.WORKOUT.replace(':id', workout.id)}
                intensity={workout.intensity}
                equipment={workout.equipment}
                isSaved={workout.isSaved}
                isCompleted={workout.isCompleted}
                testId={
                  cardTestIdPrefix
                    ? `${cardTestIdPrefix}-${idx}`
                    : testId
                      ? `${testId}-card-${idx}`
                      : `workout-rail-card-${idx}`
                }
                onClick={() => onCardClick?.(workout)}
                onSaveLesson={handleSaveLesson}
                onUnsaveLesson={handleUnsaveLesson}
              />
            ) : (
              <SimplifiedClassCard
                srcSet={workout.srcSet}
                title={workout.title}
                to={routes.WORKOUT.replace(':id', workout.id)}
                intensity={workout.intensity}
                equipment={workout.equipment}
                label={workout.label}
                isDisabled={workout.isDisabled}
                isDone={workout.isDone}
                testId={
                  cardTestIdPrefix
                    ? `${cardTestIdPrefix}-${idx}`
                    : testId
                      ? `${testId}-card-${idx}`
                      : `workout-rail-card-${idx}`
                }
                onClick={() => onCardClick?.(workout)}
              />
            )}
          </ClassCardContainer>
        );
      })}
    </Rail>
  );
};

export default WorkoutRail;
